import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { clientsApi, slotsApi } from "../../api";
import DietitianTopbar from "./DietitianTopbar";
import classes from "./DietitianDashboard.module.scss";

// ── Types (local, minimal) ────────────────────────────────
type SlotStatus = "booked" | "open" | "blocked" | "done";
type Slot = {
  id: string; date: string; time: string; timeVal: string;
  duration: number; status: SlotStatus; client?: string; type?: string;
};
type Client = {
  id: string; name: string; goal?: string;
  startWeight?: number; currentWeight?: number; targetWeight?: number;
  status: "active" | "inactive" | "pending";
  unlockedWeeks: string[];
};

const pad = (n: number) => String(n).padStart(2, "0");
const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const today = fmt(new Date());

const greetingWord = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const todayLabel = new Date().toLocaleDateString("en-US", {
  weekday: "long", month: "long", day: "numeric", year: "numeric",
});

const statusLabel: Record<SlotStatus, string> = {
  booked: "Booked", open: "Open", blocked: "Blocked", done: "Done",
};

const Spinner = () => (
  <div className={classes.spinner_wrap}><div className={classes.spinner} /></div>
);

const DietitianDashboard = () => {
  const navigate   = useNavigate();
  const { user }   = useAuth();

  const [clients,  setClients]  = useState<Client[]>([]);
  const [slots,    setSlots]    = useState<Slot[]>([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    Promise.all([
      clientsApi.list().catch(() => ({ clients: [] })),
      slotsApi.list(today).catch(() => ({ slots: [] })),
    ]).then(([cd, sd]) => {
      setClients(cd.clients ?? []);
      setSlots((sd.slots ?? []).sort((a: Slot, b: Slot) => a.timeVal.localeCompare(b.timeVal)));
    }).finally(() => setLoading(false));
  }, []);

  const activeClients  = clients.filter(c => c.status === "active").length;
  const pendingClients = clients.filter(c => c.status === "pending").length;
  const todayBooked    = slots.filter(s => s.status === "booked" || s.status === "done").length;
  const openSlots      = slots.filter(s => s.status === "open").length;

  const STATS = [
    { label: "Active Clients",   value: activeClients,  color: "green",  icon: "👥" },
    { label: "Today's Sessions", value: todayBooked,    color: "blue",   icon: "📅" },
    { label: "Pending Clients",  value: pendingClients, color: "yellow", icon: "📋" },
    { label: "Open Slots Today", value: openSlots,      color: "purple", icon: "🕐" },
  ];

  const recentClients = clients.slice(0, 5);

  const progressPct = (c: Client) => {
    if (!c.startWeight || !c.currentWeight || !c.targetWeight) return 0;
    const total = c.startWeight - c.targetWeight;
    const done  = c.startWeight - c.currentWeight;
    if (total <= 0) return 0;
    return Math.round(Math.max(0, Math.min(100, (done / total) * 100)));
  };

  const initials = (name: string) =>
    name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <>
      <DietitianTopbar
        title={`${greetingWord()}${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`}
        subtitle={todayLabel}
      />

      {/* ── Content ── */}
      <div className={classes.content}>

        {/* Stats row */}
        <div className={classes.stats_row}>
          {STATS.map(s => (
            <div key={s.label} className={`${classes.stat_card} ${classes[`stat_${s.color}`]}`}>
              <span className={classes.stat_emoji}>{s.icon}</span>
              <span className={classes.stat_value}>{loading ? "—" : s.value}</span>
              <span className={classes.stat_label}>{s.label}</span>
            </div>
          ))}
        </div>

        <div className={classes.two_col}>

          {/* Today's Schedule */}
          <section className={classes.card}>
            <div className={classes.card_header}>
              <h2 className={classes.card_title}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                Today's Schedule
              </h2>
              <button className={classes.view_btn} onClick={() => navigate("/dietitian/schedule")}>
                Manage →
              </button>
            </div>
            {loading ? <Spinner /> : slots.length === 0 ? (
              <p className={classes.empty_msg}>No slots scheduled for today.</p>
            ) : (
              <div className={classes.timeline}>
                {slots.map(s => (
                  <div key={s.id} className={`${classes.timeline_item} ${classes[`tl_${s.status}`]}`}>
                    <div className={classes.tl_time}>{s.time}</div>
                    <div className={classes.tl_dot} />
                    <div className={classes.tl_body}>
                      <div className={classes.tl_client}>
                        {s.status === "open" ? "Open Slot" : s.status === "blocked" ? "Blocked" : s.client}
                      </div>
                      {s.type && <div className={classes.tl_type}>{s.type} · {s.duration} min</div>}
                    </div>
                    <span className={`${classes.tl_badge} ${classes[`badge_${s.status}`]}`}>
                      {statusLabel[s.status]}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Recent Clients */}
          <section className={classes.card}>
            <div className={classes.card_header}>
              <h2 className={classes.card_title}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                </svg>
                Recent Clients
              </h2>
              <button className={classes.view_btn} onClick={() => navigate("/dietitian/clients")}>
                View All →
              </button>
            </div>
            {loading ? <Spinner /> : recentClients.length === 0 ? (
              <p className={classes.empty_msg}>No clients yet.</p>
            ) : (
              <div className={classes.client_list}>
                {recentClients.map(c => (
                  <div key={c.id} className={classes.client_row}>
                    <div className={`${classes.client_avatar} ${c.status === "inactive" ? classes.avatar_inactive : ""}`}>
                      {initials(c.name)}
                    </div>
                    <div className={classes.client_info}>
                      <div className={classes.client_name}>{c.name}</div>
                      <div className={classes.client_goal}>{c.goal ?? c.status}</div>
                    </div>
                    <div className={classes.client_progress}>
                      <div className={classes.progress_bar}>
                        <div className={classes.progress_fill} style={{ width: `${progressPct(c)}%` }} />
                      </div>
                      <div className={classes.progress_label}>
                        {c.currentWeight ? `${c.currentWeight} kg` : "—"}
                      </div>
                    </div>
                    <button
                      className={classes.open_btn}
                      onClick={() => navigate(`/dietitian/clients/${c.id}`)}
                    >
                      Open
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

        </div>
      </div>
    </>
  );
};

export default DietitianDashboard;
